import React from "react";
import { FileText, Building, Info, X, Paperclip } from "lucide-react";
import { Button } from "../../components/AdminSharedComponents";
import TextField from "../../components/ui/Form/TextField";
import TextareaField from "../../components/ui/Form/TextareaField";
import DropdownField from "../../components/ui/Form/DropdownField";
import FileField from "../../components/ui/Form/FileField";
import PageFormHeader from "../../components/PageFormHeader";

const ReportFormComponent = ({
  formId,
  isEdit,
  loading,
  submitting,
  title,
  backLink,
  backText,
  cancelLink,
  cancelText,
  submitText,
  loadingText,
  submitIcon,
  error,
  uploadProgress,
  formData,
  setFormData,
  selectedFiles,
  setSelectedFiles,
  filePreviews,
  setFilePreviews,
  currentAttachments,
  onRemoveAttachment,
  projects,
  isProjectLocked,
  isExcavationLocked,
  pendingRemovals,
  setPendingRemovals,
  handleChange,
  handleFileChange,
  handleRemoveFile,
  handleExistingAttachmentRemoval,
  handleUndoRemoval,
  handleSubmit,
  handleCancel,
  onFileSizeError,
}) => {
  // Calculate total file size
  const calculateTotalSize = () => {
    const newFilesSize = selectedFiles.reduce(
      (total, file) => total + file.size,
      0
    );
    const existingFilesSize = currentAttachments
      ? currentAttachments
          .filter((att) => !pendingRemovals.includes(att.id))
          .reduce((total, att) => total + (att.size || 0), 0)
      : 0;
    return newFilesSize + existingFilesSize;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const totalSize = calculateTotalSize();
  const totalFiles =
    (currentAttachments ? currentAttachments.length : 0) +
    selectedFiles.length -
    pendingRemovals.length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <PageFormHeader
        title={title}
        backLink={backLink}
        backText={backText}
        cancelLink={cancelLink}
        cancelText={cancelText}
        submitText={submitText}
        loadingText={loadingText}
        submitIcon={submitIcon}
        loading={submitting}
        formId={formId}
      />

      {/* Main Content */}
      <div className="px-4 md:px-6 py-6 md:py-8 pb-24 md:pb-8">
        <div className="max-w-7xl mx-auto">
          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <X className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form id={formId} onSubmit={handleSubmit}>
            <div className="space-y-6 md:space-y-8">
              {/* Mobile Layout - Stacked */}
              <div className="block lg:hidden space-y-6">
                {/* Report Information - Mobile */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <div className="px-4 py-4 border-b border-gray-200">
                    <h2 className="flex items-center gap-2 text-lg font-semibold text-primary-dark">
                      <FileText className="h-5 w-5" />
                      Report Information
                    </h2>
                  </div>

                  <div className="p-4">
                    <div className="space-y-4">
                      <TextField
                        label="Report Title"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        placeholder="Enter report title"
                        required
                      />

                      <TextareaField
                        label="Description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        placeholder="Describe the report content and purpose"
                        rows={3}
                        required
                      />

                      <DropdownField
                        label={
                          <span className="flex items-center gap-1">
                            <Building className="h-4 w-4" />
                            Associated Project
                          </span>
                        }
                        name="project_id"
                        value={formData.project_id}
                        onChange={handleChange}
                        options={[
                          {
                            value: "",
                            label: "-- Select Project (Optional) --",
                          },
                          ...projects.map((project) => ({
                            value: project.id,
                            label: project.name,
                          })),
                        ]}
                        disabled={isProjectLocked}
                      />

                      <DropdownField
                        label="Excavation Point (optional)"
                        name="excavation_point_id"
                        value={formData.excavation_point_id}
                        onChange={handleChange}
                        options={[
                          { value: "", label: "-- Select Excavation Point --" },
                          ...(formData.project_id
                            ? (
                                projects.find(
                                  (p) => p.id === Number(formData.project_id)
                                )?.excavation_points || []
                              ).map((point) => ({
                                value: point.id,
                                label: point.name,
                              }))
                            : []),
                        ]}
                        disabled={!formData.project_id || isExcavationLocked}
                      />
                    </div>
                  </div>
                </div>

                {/* File Upload - Mobile */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <div className="px-4 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h2 className="flex items-center gap-2 text-lg font-semibold text-primary-dark">
                        <FileText className="h-5 w-5" />
                        Attachments
                      </h2>
                      {/* File Summary Badge */}
                      <div className="flex items-center gap-2 text-xs">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                          {totalFiles}
                        </span>
                        <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full font-medium">
                          {formatFileSize(totalSize)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-4">
                    {/* Default FileField for mobile */}
                    <FileField
                      label="Document Attachments"
                      name="attachments"
                      value={selectedFiles}
                      onChange={handleFileChange}
                      onRemove={handleRemoveFile}
                      currentAttachments={currentAttachments}
                      pendingRemovals={pendingRemovals}
                      onMarkForRemoval={handleExistingAttachmentRemoval}
                      onUndoRemoval={handleUndoRemoval}
                      multiple={true}
                      accept=".png,.jpg,.jpeg,.gif,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,.7z,.tar,.gz"
                      maxSize={10}
                      disabled={submitting}
                      onFileSizeError={onFileSizeError}
                    />

                    {/* Upload Progress - Mobile */}
                    {(submitting || loading) && uploadProgress > 0 && (
                      <div className="mt-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-gray-700">
                            Uploading...
                          </span>
                          <span className="text-sm text-gray-500">
                            {uploadProgress}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Upload Guidelines - Mobile */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                      <div className="flex items-start gap-2">
                        <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                        <div className="text-xs text-blue-800">
                          <p className="font-medium mb-1">Guidelines:</p>
                          <p>
                            Max 10MB each • Images, PDF, Office docs, Archives •
                            Multiple files OK
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop Layout - 2 Columns */}
              <div className="hidden lg:block">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left Column - Report Information (Sticky) */}
                  <div className="sticky top-24 self-start">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                      <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="flex items-center gap-2 text-lg font-semibold text-primary-dark">
                          <FileText className="h-5 w-5" />
                          Report Information
                        </h2>
                      </div>

                      <div className="p-6">
                        <div className="space-y-6">
                          <TextField
                            label="Report Title"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            placeholder="Enter report title"
                            required
                          />

                          <TextareaField
                            label="Description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            placeholder="Describe the report content and purpose"
                            rows={4}
                            required
                          />

                          <DropdownField
                            label={
                              <span className="flex items-center gap-1">
                                <Building className="h-4 w-4" />
                                Associated Project
                              </span>
                            }
                            name="project_id"
                            value={formData.project_id}
                            onChange={handleChange}
                            options={[
                              {
                                value: "",
                                label: "-- Select Project (Optional) --",
                              },
                              ...projects.map((project) => ({
                                value: project.id,
                                label: project.name,
                              })),
                            ]}
                            disabled={isProjectLocked}
                          />

                          <DropdownField
                            label="Excavation Point (optional)"
                            name="excavation_point_id"
                            value={formData.excavation_point_id}
                            onChange={handleChange}
                            options={[
                              {
                                value: "",
                                label: "-- Select Excavation Point --",
                              },
                              ...(formData.project_id
                                ? (
                                    projects.find(
                                      (p) =>
                                        p.id === Number(formData.project_id)
                                    )?.excavation_points || []
                                  ).map((point) => ({
                                    value: point.id,
                                    label: point.name,
                                  }))
                                : []),
                            ]}
                            disabled={
                              !formData.project_id || isExcavationLocked
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - File Upload */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <h2 className="flex items-center gap-2 text-lg font-semibold text-primary-dark">
                          <Paperclip className="h-5 w-5" />
                          Document Attachments
                        </h2>
                        {/* File Summary Badge */}
                        <div className="flex items-center gap-2 text-sm">
                          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-medium">
                            {totalFiles} files
                          </span>
                          <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-medium">
                            {formatFileSize(totalSize)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="p-6">
                      <div className="space-y-6">
                        {/* File Upload Component - Default variant */}
                        <FileField
                          label="Document Attachments"
                          name="attachments"
                          value={selectedFiles}
                          previews={filePreviews}
                          onChange={handleFileChange}
                          onRemove={handleRemoveFile}
                          currentAttachments={currentAttachments}
                          pendingRemovals={pendingRemovals}
                          onMarkForRemoval={handleExistingAttachmentRemoval}
                          onUndoRemoval={handleUndoRemoval}
                          multiple={true}
                          accept=".png,.jpg,.jpeg,.gif,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,.7z,.tar,.gz"
                          maxSize={10}
                          disabled={submitting}
                          onFileSizeError={onFileSizeError}
                        />

                        {/* Upload Progress */}
                        {(submitting || loading) && uploadProgress > 0 && (
                          <div className="mt-4">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm font-medium text-gray-700">
                                Uploading files...
                              </span>
                              <span className="text-sm text-gray-500">
                                {uploadProgress}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-primary h-2 rounded-full transition-all duration-300"
                                style={{ width: `${uploadProgress}%` }}
                              />
                            </div>
                          </div>
                        )}

                        {/* Upload Instructions */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-start gap-2">
                            <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            <div className="space-y-2 text-sm text-blue-800">
                              <p className="font-medium">Upload Guidelines:</p>
                              <ul className="list-disc list-inside space-y-1 ml-2 text-blue-700">
                                <li>Max: 10MB each</li>
                                <li>Images, PDF, Office docs, Archives</li>
                                <li>Multiple files supported</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReportFormComponent;
